// Premium Design System for Salgsklare Products

export const designSystem = {
  // Format options
  formats: {
    a4: { width: 2480, height: 3508, dpi: 300, unit: 'mm', label: 'A4 (210 x 297 mm)' },
    letter: { width: 2550, height: 3300, dpi: 300, unit: 'in', label: 'US Letter (8.5 x 11")' },
    pinterest: { width: 1000, height: 1500, dpi: 72, unit: 'px', label: 'Pinterest Pin' },
  },

  // Premium Color Palettes
  colorPalettes: {
    'peachy-calm': {
      name: 'Peachy Calm',
      primary: '#E8B4A0',
      secondary: '#F5D5CB',
      accent: '#C9A193',
      background: '#FFF8F0',
      text: '#2C2C2C',
      textLight: '#6B6B6B',
      border: '#FFE4D6',
      success: '#9BC4A8'
    },
    'minimal-elegant': {
      name: 'Minimal Elegant',
      primary: '#2C2C2C',
      secondary: '#4A4A4A',
      accent: '#8B7355',
      background: '#FFFFFF',
      text: '#1A1A1A',
      textLight: '#666666',
      border: '#E5E5E5',
      success: '#7FA99B'
    },
    'soft-lavender': {
      name: 'Soft Lavender',
      primary: '#C9B4E8',
      secondary: '#E8D4F0',
      accent: '#A89BC9',
      background: '#FAF8FF',
      text: '#2C2C2C',
      textLight: '#6B6B6B',
      border: '#E8D4F0',
      success: '#A8C9B4'
    },
    'nature-green': {
      name: 'Nature Green',
      primary: '#7FA99B',
      secondary: '#B8D5CA',
      accent: '#5A8A7A',
      background: '#F5FAF8',
      text: '#2C2C2C',
      textLight: '#5A6B65',
      border: '#D5E8E0',
      success: '#9BC4A8'
    },
    'warm-terracotta': {
      name: 'Warm Terracotta',
      primary: '#D4806A',
      secondary: '#F0C4B8',
      accent: '#B86A55',
      background: '#FFF5F2',
      text: '#2C2C2C',
      textLight: '#6B5B55',
      border: '#F0D4CA',
      success: '#A8B49C'
    }
  },

  // Typography System
  typography: {
    fonts: {
      serif: {
        heading: '"Playfair Display", serif',
        body: '"Crimson Text", serif'
      },
      sansSerif: {
        heading: '"Inter", sans-serif',
        body: '"Inter", sans-serif'
      },
      modern: {
        heading: '"Montserrat", sans-serif',
        body: '"Open Sans", sans-serif'
      },
      elegant: {
        heading: '"Cormorant Garamond", serif',
        body: '"Lora", serif'
      }
    },
    sizes: {
      cover: {
        title: { size: 64, weight: 700, lineHeight: 1.2 },
        subtitle: { size: 28, weight: 400, lineHeight: 1.4 }
      },
      page: {
        title: { size: 36, weight: 600, lineHeight: 1.3 },
        subtitle: { size: 20, weight: 500, lineHeight: 1.4 },
        body: { size: 14, weight: 400, lineHeight: 1.6 },
        label: { size: 12, weight: 600, lineHeight: 1.4 },
        small: { size: 10, weight: 400, lineHeight: 1.5 }
      }
    }
  },

  // Spacing System (in pixels for 300 DPI)
  spacing: {
    page: {
      marginTop: 120,
      marginBottom: 120,
      marginLeft: 100,
      marginRight: 100,
      sectionGap: 60,
      elementGap: 30
    },
    cover: {
      padding: 150,
      elementGap: 40
    }
  },

  // Component Templates
  components: {
    cover: {
      layouts: ['centered', 'top-aligned', 'minimal', 'decorative'],
      elements: ['title', 'subtitle', 'decorative-line', 'icon', 'year']
    },
    tracker: {
      layouts: ['table', 'grid', 'list'],
      elements: ['header', 'rows', 'checkboxes', 'date-fields', 'notes']
    },
    worksheet: {
      layouts: ['prompts', 'free-form', 'structured'],
      elements: ['questions', 'lines', 'boxes', 'ratings']
    }
  },

  // Page Types with Design Specs
  pageTypes: {
    cover: {
      name: 'Cover Page',
      elements: ['title', 'subtitle', 'decorative'],
      backgroundColor: 'primary',
      textColor: 'white'
    },
    intro: {
      name: 'Introduction',
      elements: ['title', 'body', 'welcome-box'],
      layout: 'single-column'
    },
    tracker: {
      name: 'Tracker',
      elements: ['title', 'table', 'legend'],
      layout: 'table-grid'
    },
    worksheet: {
      name: 'Worksheet',
      elements: ['title', 'prompts', 'lines'],
      layout: 'structured-prompts'
    },
    notes: {
      name: 'Notes',
      elements: ['title', 'lines'],
      layout: 'lined-paper'
    }
  },

  // Design Patterns
  patterns: {
    budgetTracker: {
      columns: ['Category', 'Budget', 'Actual', 'Diff', 'Notes'],
      rowHeight: 50,
      headerHeight: 60
    },
    habitTracker: {
      columns: 31, // days
      rowHeight: 40,
      checkboxSize: 30
    },
    mealPlanner: {
      days: 7,
      meals: ['Breakfast', 'Lunch', 'Dinner', 'Snacks'],
      cellHeight: 80
    }
  }
};

// Helper functions
export const getColorPalette = (paletteId = 'peachy-calm') => {
  return designSystem.colorPalettes[paletteId] || designSystem.colorPalettes['peachy-calm'];
};

export const getTypography = (style = 'modern') => {
  return designSystem.typography.fonts[style] || designSystem.typography.fonts.modern;
};

export const getFormat = (format = 'a4') => {
  return designSystem.formats[format] || designSystem.formats.a4;
};